import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { useKibanaLogger } from '../../../../shared/hooks/kibana-logger/kibana-logger';
import {
  fetchDataBuilderResults,
  verifyDataBuilderPayload,
} from '../../api/data-builder-api';
import { withDataBuilderMutationPolicy } from '../../api/data-builder-mutation-policy';
import { useBuildDataBuilderPayload } from '../../api/useBuildDataBuilderPayload';
import { useDataBuilderQuery } from '../../api/useDataBuilderQuery';
import {
  selectCompanyMapperGateStatus,
  selectDeliverableClient,
  selectDeliverableName,
  selectDeliveryConfigured,
  selectDeliveryMethod,
  useDataBuilderSelector,
} from '../../data-builder.store';

const FALLBACK_ERROR_MESSAGE = 'Validation failed.';

const isValidationErrorDetail = (
  detail: unknown
): detail is { msg: string }[] => {
  if (!Array.isArray(detail) || detail.length === 0) {
    return false;
  }
  return detail.every(
    (item) =>
      item !== null &&
      typeof item === 'object' &&
      'msg' in item &&
      typeof (item as { msg: unknown }).msg === 'string'
  );
};

const extractErrorMessage = (error: unknown): string => {
  if (!(error instanceof Error) || error.message.length === 0) {
    return FALLBACK_ERROR_MESSAGE;
  }
  try {
    const parsed: unknown = JSON.parse(error.message);
    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      !('detail' in parsed)
    ) {
      return error.message;
    }
    const detail = (parsed as { detail: unknown }).detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (isValidationErrorDetail(detail)) {
      return detail.map((e) => e.msg).join(' ');
    }
  } catch {
    // not JSON, fall through to raw message
  }
  return error.message;
};

type UseVerifyDataBuilderResult = {
  isPending: boolean;
  isError: boolean;
  errorMessage: string;
  isReady: boolean;
  verifyAsync: () => Promise<void>;
};

export const useVerifyDataBuilder = (): UseVerifyDataBuilderResult => {
  const { kibanaLogger } = useKibanaLogger();
  const { data } = useDataBuilderQuery();
  const { data: rawSchema } = useQuery({
    queryKey: ['data-builder'],
    queryFn: fetchDataBuilderResults,
  });

  const deliveryMethod = useDataBuilderSelector(selectDeliveryMethod);
  const deliveryConfigured = useDataBuilderSelector(selectDeliveryConfigured);
  const storedName = useDataBuilderSelector(selectDeliverableName);
  const storedClient = useDataBuilderSelector(selectDeliverableClient);
  const companySelectionGateStatus = useDataBuilderSelector(
    selectCompanyMapperGateStatus
  );
  const isWaitingForCompanies = companySelectionGateStatus === 'pending';

  const trimmedName = storedName?.trim() ?? '';
  const payloadName = trimmedName.length > 0 ? trimmedName : 'Data Builder';
  const payloadClient = storedClient !== '' ? storedClient : null;

  const { build } = useBuildDataBuilderPayload({
    client: payloadClient,
    name: payloadName,
    datasets: data?.datasets,
  });

  const {
    mutate,
    mutateAsync,
    isPending,
    isError,
    isSuccess,
    error,
    variables,
  } = useMutation(
    withDataBuilderMutationPolicy(
      {
        mutationKey: ['data-builder', 'verify'],
        mutationFn: verifyDataBuilderPayload,
      },
      kibanaLogger
    )
  );

  const buildRef = useRef(build);
  buildRef.current = build;

  const isDownloadMethod = deliveryMethod === 'download';
  const payloadResult =
    rawSchema !== undefined && deliveryConfigured && isDownloadMethod
      ? buildRef.current(rawSchema)
      : null;
  const payloadSignature =
    payloadResult === null ? null : JSON.stringify(payloadResult.payload);

  // Auto-verify is only used for the download method, where verifications are
  // triggered by format changes and feed the Continue button's enabled state.
  // Non-download methods verify explicitly via verifyAsync on Continue.
  useEffect(() => {
    if (
      !isDownloadMethod ||
      rawSchema === undefined ||
      !deliveryConfigured ||
      payloadSignature === null ||
      isWaitingForCompanies
    ) {
      return;
    }
    const next = buildRef.current(rawSchema);
    if (next === null) {
      return;
    }
    mutate(next.payload);
  }, [
    isDownloadMethod,
    payloadSignature,
    rawSchema,
    deliveryConfigured,
    isWaitingForCompanies,
    mutate,
  ]);

  const verifyAsync = useCallback(async () => {
    if (rawSchema === undefined || isWaitingForCompanies) {
      throw new Error(FALLBACK_ERROR_MESSAGE);
    }
    const next = buildRef.current(rawSchema);
    if (next === null) {
      throw new Error(FALLBACK_ERROR_MESSAGE);
    }
    await mutateAsync(next.payload);
  }, [isWaitingForCompanies, mutateAsync, rawSchema]);

  const errorMessage = extractErrorMessage(error);
  const verifiedPayloadSignature =
    variables === undefined ? null : JSON.stringify(variables);

  return {
    isPending,
    isError,
    errorMessage,
    isReady:
      isSuccess &&
      !isPending &&
      !isError &&
      !isWaitingForCompanies &&
      payloadSignature !== null &&
      verifiedPayloadSignature === payloadSignature,
    verifyAsync,
  };
};
